// MCP admin client: MCP servers, MCP server groups, and group membership.
//
// Built against the real AiMcp backend, confirmed by reading the controllers directly:
//   BaseMcpServerController            api/v1/ai-mcp/mcp-servers
//   BaseMcpServerGroupController       api/v1/ai-mcp/mcp-server-groups
//   BaseMcpServerGroupMemberController api/v1/ai-mcp/mcp-server-group-members
//   BaseMcpServerTypeController        api/v1/ai-mcp/mcp-server-types
//   BaseMcpServerCategoryController    api/v1/ai-mcp/mcp-server-categories
//
// Wire-shape rules follow teamAdminApiClient.ts: Create/Update send entity fields nested under
// `data` (post/put), while action and search requests are root-level (postQuery/del). Group-member
// actions bind IDInfo fields, so IDs go out as { ID: n }.

import { BaseAdminApiClient, MAX_PAGE_SIZE, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type {
  LookupOption,
  McpServerDTO,
  McpServerFormValues,
  McpServerGroupDTO,
  McpServerGroupFormValues,
  McpServerGroupMemberDTO,
} from '../types';

const PAGE_ALL = { PageNumber: 1, PageSize: MAX_PAGE_SIZE };

export class McpAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  // --- MCP Server ---

  async getAllServers(): Promise<AdminApiResult<McpServerDTO[]>> {
    const result = await this.postQuery<McpServerDTO[]>('/api/v1/ai-mcp/mcp-servers/list', { PageInfo: PAGE_ALL });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  /** One page of MCP servers (all, including inactive) for the MCP Servers list. Server-paged. */
  async getServersPage(page: number, pageSize: number): Promise<AdminApiResult<McpServerDTO[]>> {
    const result = await this.postQuery<McpServerDTO[]>('/api/v1/ai-mcp/mcp-servers/list', { PageInfo: { PageNumber: page, PageSize: pageSize } });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async getServerById(serverId: number): Promise<AdminApiResult<McpServerDTO>> {
    return this.postQuery<McpServerDTO>(`/api/v1/ai-mcp/mcp-servers/${serverId}`, { ID: { ID: serverId } });
  }

  async createServer(server: McpServerFormValues): Promise<AdminApiResult<McpServerDTO>> {
    const response = await this.post<{ id?: { id?: number } }>('/api/v1/ai-mcp/mcp-servers', server);
    const newID = response.data?.id?.id;
    if (response.success && newID) {
      return { ...response, data: { McpServerID: newID, ...server } as McpServerDTO };
    }
    return response as unknown as AdminApiResult<McpServerDTO>;
  }

  async updateServer(serverId: number, server: Partial<McpServerFormValues>): Promise<AdminApiResult<McpServerDTO>> {
    const response = await this.put<unknown>(`/api/v1/ai-mcp/mcp-servers/${serverId}`, { McpServerID: serverId, ...server });
    if (response.success) {
      return { ...response, data: { McpServerID: serverId, ...server } as McpServerDTO };
    }
    return response as unknown as AdminApiResult<McpServerDTO>;
  }

  async deleteServer(serverId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-mcp/mcp-servers/${serverId}`, { id: serverId });
  }

  async activateServer(serverId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>(`/api/v1/ai-mcp/mcp-servers/${serverId}/activate`, { McpServerID: { ID: serverId } });
  }

  async deactivateServer(serverId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>(`/api/v1/ai-mcp/mcp-servers/${serverId}/deactivate`, { McpServerID: { ID: serverId } });
  }

  // Lookups for the server form. Both controllers expose an `active` search.
  async getServerTypes(): Promise<AdminApiResult<LookupOption[]>> {
    return this.getActiveLookup('/api/v1/ai-mcp/mcp-server-types/active', 'McpServerTypeID');
  }

  async getServerCategories(): Promise<AdminApiResult<LookupOption[]>> {
    return this.getActiveLookup('/api/v1/ai-mcp/mcp-server-categories/active', 'McpServerCategoryID');
  }

  private async getActiveLookup(endpoint: string, idField: string): Promise<AdminApiResult<LookupOption[]>> {
    const result = await this.postQuery<Array<Record<string, unknown>>>(endpoint, { PageInfo: { PageNumber: 1, PageSize: MAX_PAGE_SIZE } });
    if (!result.success || !Array.isArray(result.data)) return { ...result, data: [] };
    const options = result.data.map((item) => ({
      id: (item[idField] as number) ?? 0,
      name: (item.Name as string) ?? String(item[idField] ?? ''),
      ...item,
    }));
    return { success: true, data: options };
  }

  // --- MCP Server Group ---
  // A group is identified by McpServerGroupID. The backend has no group name.

  async getAllGroups(): Promise<AdminApiResult<McpServerGroupDTO[]>> {
    const result = await this.postQuery<McpServerGroupDTO[]>('/api/v1/ai-mcp/mcp-server-groups/list', { PageInfo: PAGE_ALL });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  /** One page of server groups for the MCP Server Groups list. Server-paged. */
  async getGroupsPage(page: number, pageSize: number): Promise<AdminApiResult<McpServerGroupDTO[]>> {
    const result = await this.postQuery<McpServerGroupDTO[]>('/api/v1/ai-mcp/mcp-server-groups/list', { PageInfo: { PageNumber: page, PageSize: pageSize } });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async getGroupById(groupId: number): Promise<AdminApiResult<McpServerGroupDTO>> {
    return this.postQuery<McpServerGroupDTO>(`/api/v1/ai-mcp/mcp-server-groups/${groupId}`, { ID: { ID: groupId } });
  }

  async createGroup(group: McpServerGroupFormValues): Promise<AdminApiResult<McpServerGroupDTO>> {
    const response = await this.post<{ id?: { id?: number } }>('/api/v1/ai-mcp/mcp-server-groups', group);
    const newID = response.data?.id?.id;
    if (response.success && newID) {
      return { ...response, data: { McpServerGroupID: newID, ...group } as McpServerGroupDTO };
    }
    return response as unknown as AdminApiResult<McpServerGroupDTO>;
  }

  async updateGroup(groupId: number, group: Partial<McpServerGroupFormValues>): Promise<AdminApiResult<McpServerGroupDTO>> {
    const response = await this.put<unknown>(`/api/v1/ai-mcp/mcp-server-groups/${groupId}`, { McpServerGroupID: groupId, ...group });
    if (response.success) {
      return { ...response, data: { McpServerGroupID: groupId, ...group } as McpServerGroupDTO };
    }
    return response as unknown as AdminApiResult<McpServerGroupDTO>;
  }

  async deleteGroup(groupId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-mcp/mcp-server-groups/${groupId}`, { id: groupId });
  }

  // --- MCP Server Group membership ---

  async getMembersByGroup(groupId: number): Promise<AdminApiResult<McpServerGroupMemberDTO[]>> {
    const result = await this.postQuery<McpServerGroupMemberDTO[]>(
      `/api/v1/ai-mcp/mcp-server-group-members/group/${groupId}`,
      { McpServerGroupID: { ID: groupId }, PageInfo: PAGE_ALL }
    );
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async addServerToGroup(groupId: number, serverId: number, displayOrder: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-mcp/mcp-server-group-members/add-server-to-group', {
      McpServerGroupID: { ID: groupId },
      McpServerID: { ID: serverId },
      DisplayOrder: displayOrder,
    });
  }

  async removeServerFromGroup(groupId: number, serverId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>('/api/v1/ai-mcp/mcp-server-group-members/remove-server-from-group', {
      McpServerGroupID: { ID: groupId },
      McpServerID: { ID: serverId },
    });
  }
}
